import { styled } from '@linaria/react';
import { motion } from 'framer-motion';
import { Search as SearchIcon, X as XIcon } from 'lucide-react';

import useHasHydrated from '@/hooks/use-has-hydrated';

import Spinner from '@/components/Spinner';
import VisuallyHidden from '@/components/VisuallyHidden';
import Spacer from '@/components/Spacer';
import Boop from '@/components/Boop';

import BigTextInput from './BigTextInput';

const HEIGHT = 59;
const ICON_SIZE = 24;

const Search = ({
  prefersReducedMotion,
  searchValue,
  isFocused,
  handleSearch,
  handleClear,
  handleFocus,
}) => {
  const hasHydrated = useHasHydrated();

  return (
    <Wrapper layout={!prefersReducedMotion}>
      <InputLabel data-is-focused={isFocused}>
        <TranslucentSearchIcon size={ICON_SIZE} />
        <VisuallyHidden>Search for an operator</VisuallyHidden>
        {hasHydrated ? (
          <BigTextInput
            searchValue={searchValue}
            handleSearch={handleSearch}
            handleFocus={handleFocus}
            handleClear={handleClear}
          />
        ) : (
          <SpinnerWrapper>
            <Spinner size={24} />
          </SpinnerWrapper>
        )}
        {searchValue ? (
          <Boop rotation={20}>
            <CloseButton onClick={handleClear}>
              <VisuallyHidden>Clear search term</VisuallyHidden>
              <XIcon size={ICON_SIZE} />
            </CloseButton>
          </Boop>
        ) : (
          <Spacer size={ICON_SIZE} />
        )}
      </InputLabel>
      <BackgroundBox style={{ opacity: isFocused ? 1 : 0 }} />
    </Wrapper>
  );
};

const Wrapper = styled(motion.div)`
  padding: 2px;
  will-change: transform;
`;

const SpinnerWrapper = styled.div`
  flex: 1;
  display: grid;
  place-content: center;
  height: ${HEIGHT}px;
  opacity: 0.4;
`;

const BackgroundBox = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  transition: opacity 600ms;
  /* This is necessary so that when the search input moves in front of the header during page transition, the input effectively covers the header. */
  background: var(--color-background);
  box-shadow:
    0 0.9px 1.2px rgba(0, 0, 0, 0.064),
    0 2.1px 2.9px rgba(0, 0, 0, 0.07),
    0 3.9px 5.5px rgba(0, 0, 0, 0.072),
    0 6.9px 9.8px rgba(0, 0, 0, 0.076),
    0 13px 18.4px rgba(0, 0, 0, 0.092),
    0 31px 44px rgba(0, 0, 0, 0.17),
    0 -5px 15px rgba(0, 0, 0, 0.1);
`;

const InputLabel = styled.label`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 0 12px 4px;
  border-radius: 8px;
  overflow: hidden;
  color: var(--color-text);

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 4px;
    background: var(--color-gray-1000);
    border-radius: 100px;

    html[data-color-mode='dark'] &[data-is-focused='true'] {
      background: var(--color-primary);
    }
  }
`;

const TranslucentSearchIcon = styled(SearchIcon)`
  opacity: 0.75;
`;

const CloseButton = styled.button`
  color: inherit;
  opacity: 0.75;

  &:hover {
    color: var(--color-secondary);
    opacity: 1;
  }

  i,
  svg {
    display: block !important;
  }
`;

export default Search;
